import React from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { curry, propFunc } from 'util/functions'
import { Flex, ValidatedInput, SUI } from 'shared/ui'
import Attachments from 'components/Attachments'
import MentionSelector from 'components/MentionSelector'
import Composer from 'components/App/DesktopView/shared/Composer/LogConversation'
import RecipientsEditor from 'components/App/DesktopView/shared/Composer/Header/RecipientsEditor'
import MailboxSelectorDropdown from '../../shared/MailboxSelectorDropdown'

const EditorContainer = styled('div')`
  width: 100%;
  height: 100%;
  position: relative;

  && .editorAndAttachments {
    max-height: 60vh;
    margin-bottom: ${props => props.theme.spacing['5']};
  }

  && .mce-content-body {
    height: 100%;
  }

  && [class*='Attachments__'] {
    flex-shrink: 0;
    max-height: 190px; /* enough to show one row of images and regular attachments */
    overflow: auto;
  }
`

const MentionWrapper = styled('div')`
  position: absolute;
  bottom: 100%;
  width: 100%;
`

const SuifyiedValidatedInput = ({ className, ...rest }) => (
  <SUI className={className}>
    <ValidatedInput {...rest} />
  </SUI>
)

const Input = styled(SuifyiedValidatedInput)`
  width: 100%;
`

const FormRow = styled(Flex)`
  margin-bottom: ${props => props.theme.spacing['10']};
  align-items: center;
  &:last-child {
    margin-bottom: 0;
  }
`

const LastFormRow = styled(FormRow)`
  flex-grow: 1;
  height: 100%;
  min-height: 0;
`

const FieldLabel = styled('div')`
  color: ${props => props.theme.color.monochrome.mediumDark};
  min-width: 51px;
  margin-right: ${props => props.theme.spacing['12']};
`
const ShowHideButton = styled('div')`
  line-height: 1.5;
  text-transform: uppercase;
  color: ${props => props.theme.color.monochrome.mediumDark};
  font-size: ${props => props.theme.spacing['7']};
  flex-shrink: 0;
  flex-grow: 0;
  cursor: pointer;
`

const StyledDropdown = styled(MailboxSelectorDropdown)`
  &&& {
    .text {
      margin-top: -2px;
      margin-bottom: -5px;
      line-height: 1.5;
      white-space: nowrap;
      overflow-x: hidden;
      text-overflow: ellipsis;
      width: 100%;
    }
    .menu {
      .item {
        white-space: nowrap !important;
        word-wrap: normal;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
`

export default class LogConvoForm extends React.Component {
  state = {
    shouldNotUpdateRecipient: false,
    optionalFields: {
      phone: false,
    },
  }

  componentWillUnmount() {
    this.resetTimer()
  }

  resetTimer = () => {
    if (this.timerID) clearTimeout(this.timerID)
  }

  showField = field => () =>
    this.setState({ optionalFields: { [field]: true } })

  saveNameInputRef = node => (this.nameInput = node)

  maybeUpdateToRecipient = (field, val) => {
    // We don't want to update recipient if input lost focus just for a moment
    // e.g. because user clicked an option in dropdown
    this.timerID = setTimeout(() => this.updateToRecipient(field)(val), 200)
  }

  updateToRecipient = field => val => {
    const { ticketId, updateRecipientDelayed, draft } = this.props
    const { to } = draft
    const last = Array.isArray(to) ? to[to.length - 1] : to
    // old semantics - we assume only one recipient (!)
    return updateRecipientDelayed(ticketId, { ...last, [field]: val })
  }

  handleNameChange = event =>
    event &&
    event.target &&
    this.maybeUpdateToRecipient('name', event.target.value)
  handleEmailChange = event =>
    event &&
    event.target &&
    this.maybeUpdateToRecipient('email', event.target.value)

  handleOnChange = func => event =>
    event && event.target && func(event.target.value)

  handleDeleteDraft = () => {
    this.resetTimer()
    this.nameInput.focus()
  }

  render() {
    const {
      draft,
      showMentionSelector,
      updateDraft,
      ticketId,
      updateAndSendDraft,
      isDraftSendable,
    } = this.props
    const recipient = (draft && draft.to && draft.to[0]) || {}
    const { phone_number: phone } = recipient
    const showPhoneField = this.state.optionalFields.phone || phone
    return (
      <Flex
        className="grui flex-col"
        css={css`
          height: 100%;
        `}
      >
        <FormRow>
          <FieldLabel>To:</FieldLabel>
          <RecipientsEditor
            key={`recipientseditor-to-name-${ticketId}-note`}
            css={css`
              flex-grow: 0;
            `}
            sui
            forwardRef={this.saveNameInputRef}
            type="to"
            toType="name"
            ticketId={ticketId}
            placeholder="Customer’s full name"
            recipients={draft.to}
            onFocus={this.resetTimer}
            onBlur={this.handleNameChange}
          />
          <RecipientsEditor
            key={`recipientseditor-to-email-${ticketId}-note`}
            css={css`
              flex-grow: 0;
              min-height: 0;
            `}
            sui
            noAutoFocus
            type="to"
            toType="email"
            ticketId={ticketId}
            placeholder="Email address"
            recipients={draft.to}
            onFocus={this.resetTimer}
            onBlur={this.handleEmailChange}
          />
          {!showPhoneField && (
            <Flex
              className="grui items-center justify-end"
              css={css`
                flex-grow: 1;
              `}
            >
              <ShowHideButton onClick={this.showField('phone')}>
                Phone
              </ShowHideButton>
            </Flex>
          )}
        </FormRow>
        {showPhoneField && (
          <FormRow>
            <FieldLabel>Phone:</FieldLabel>
            <Input
              defaultValue={phone}
              placeholder="Customer's phone number"
              fluid
              onChange={this.handleOnChange(
                this.updateToRecipient('phone_number')
              )}
              renderDefaultValueChanges
            />
          </FormRow>
        )}
        <FormRow>
          <FieldLabel>From:</FieldLabel>
          <StyledDropdown
            size="small"
            fluid
            onChange={propFunc(curry(updateDraft)(ticketId)('mailboxId'))}
            value={draft.mailboxId || null}
            selection
          />
        </FormRow>
        <FormRow>
          <FieldLabel>Subject:</FieldLabel>
          <Input
            defaultValue={(draft && draft.title) || ''}
            placeholder="Enter your title here"
            fluid
            onChange={this.handleOnChange(
              curry(updateDraft)(ticketId)('title')
            )}
            renderDefaultValueChanges
          />
        </FormRow>
        <LastFormRow>
          <FieldLabel />
          <EditorContainer>
            <MentionWrapper key="mentionwrapper">
              {showMentionSelector && <MentionSelector />}
            </MentionWrapper>
            {draft && (
              <Composer
                draft={draft}
                ticketId={ticketId}
                onDeleteDraft={this.handleDeleteDraft}
                updateAndSendDraft={updateAndSendDraft}
                key="noteform"
                isDraftSendable={isDraftSendable}
                focusWhenInitialized={false}
                editorBottom={
                  <Attachments forDraft forNote key="attachments" />
                }
              />
            )}
          </EditorContainer>
        </LastFormRow>
      </Flex>
    )
  }
}
